import { createFileRoute, Link } from "@tanstack/react-router";
import { Gift, Flame, Wallet, ShieldCheck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { REFERRALS_OG_IMAGE, REFERRALS_OG_IMAGE_ALT, buildPageMetadata } from "@/lib/seo";
import { useT } from "@/i18n";

export const Route = createFileRoute("/referrals")({
  head: () =>
    buildPageMetadata({
      path: "/referrals",
      title: "Referral Program — Earn Heat for Every Invite | BeHot.Now",
      description:
        "Earn 2 Heat when someone joins with your BeHot.Now link and 10 Heat for every US$100 they spend. Reward Heat is internal credit: spend it on boosts and on Marketplace purchases.",
      keywords: [
        "referral program",
        "earn heat",
        "invite friends rewards",
        "referral link",
        "affiliate rewards",
        "creator referral bonus",
      ],
      ogTitle: "Referrals — earn Heat for every invite",
      ogDescription: "2 Heat per signup. 10 Heat per US$100 they spend.",
      image: REFERRALS_OG_IMAGE,
      imageAlt: REFERRALS_OG_IMAGE_ALT,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Referrals", path: "/referrals" },
      ],
    }),
  component: ReferralsInfo,
});

const STEP_COUNT = 4;
const FAQ_KEYS = ["a", "b", "c", "d", "e"];
const examples = [
  { invites: 10, buyers: 4, purchases: 3 },
  { invites: 50, buyers: 20, purchases: 4 },
  { invites: 250, buyers: 90, purchases: 6 },
];

function ReferralsInfo() {
  const { t } = useT();

  const steps = Array.from({ length: STEP_COUNT }, (_, i) => ({
    title: t(`marketing.referrals.step.${i}.title`),
    body: t(`marketing.referrals.step.${i}.body`),
  }));

  return (
    <div className="mx-auto max-w-4xl px-4 py-12">
      <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        <Gift className="size-3.5 text-primary" /> {t("marketing.referrals.badge")}
      </span>
      <h1 className="mt-4 font-display text-4xl font-bold sm:text-5xl">
        {t("marketing.referrals.h1_pre")} <span className="text-gradient-hot">{t("marketing.referrals.h1_grad")}</span>
      </h1>
      <p className="mt-3 max-w-2xl text-muted-foreground">
        {t("marketing.referrals.subtitle")}
      </p>

      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        <div className="card-surface p-6">
          <p className="font-display text-3xl font-bold text-primary">2 Heat</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("marketing.referrals.stat1_v")}
          </p>
        </div>
        <div className="card-surface p-6">
          <p className="font-display text-3xl font-bold text-primary">10 Heat</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("marketing.referrals.stat2_v")}
          </p>
        </div>
        <div className="card-surface p-6">
          <p className="font-display text-3xl font-bold text-primary">{t("marketing.referrals.stat3_k")}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("marketing.referrals.stat3_v")}
          </p>
        </div>
      </div>

      <h2 className="mt-12 font-display text-2xl font-bold">{t("marketing.referrals.how_heading")}</h2>
      <div className="mt-4 space-y-3">
        {steps.map((s, i) => (
          <div key={s.title} className="card-surface flex gap-4 p-5">
            <div className="rank-numeral text-2xl leading-none">{String(i + 1).padStart(2, "0")}</div>
            <div>
              <p className="font-display font-semibold">{s.title}</p>
              <p className="mt-1 text-sm text-muted-foreground">{s.body}</p>
            </div>
          </div>
        ))}
      </div>

      <h2 className="mt-12 font-display text-2xl font-bold">{t("marketing.referrals.examples_heading")}</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        {t("marketing.referrals.examples_note")}
      </p>
      <div className="card-surface mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border text-left text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="p-4">{t("marketing.referrals.table.signups")}</th>
              <th className="p-4">{t("marketing.referrals.table.buyers")}</th>
              <th className="p-4">{t("marketing.referrals.table.purchases")}</th>
              <th className="p-4">{t("marketing.referrals.table.heat")}</th>
              <th className="p-4">{t("marketing.referrals.table.credit")}</th>
            </tr>
          </thead>
          <tbody>
            {examples.map((e) => {
              const heat = e.invites * 3 + e.buyers * e.purchases;
              return (
                <tr key={e.invites} className="border-b border-border last:border-0">
                  <td className="p-4">{e.invites}</td>
                  <td className="p-4">{e.buyers}</td>
                  <td className="p-4">{e.purchases}</td>
                  <td className="p-4 font-semibold text-primary">{heat} Heat</td>
                  <td className="p-4">US${heat.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-12 grid gap-4 sm:grid-cols-2">
        <div className="card-surface p-6">
          <Wallet className="size-6 text-primary" />
          <h3 className="mt-3 font-display text-lg font-bold">{t("marketing.referrals.where_title")}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("marketing.referrals.where_body")}
          </p>
        </div>
        <div className="card-surface p-6">
          <ShieldCheck className="size-6 text-success" />
          <h3 className="mt-3 font-display text-lg font-bold">{t("marketing.referrals.rules_title")}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("marketing.referrals.rules_body")}
          </p>
        </div>
      </div>

      <h2 className="mt-12 font-display text-2xl font-bold">{t("marketing.referrals.faq_heading")}</h2>
      <Accordion type="single" collapsible className="mt-4">
        {FAQ_KEYS.map((k) => (
          <AccordionItem value={k} key={k}>
            <AccordionTrigger>{t(`marketing.referrals.faq.${k}.q`)}</AccordionTrigger>
            <AccordionContent>
              {t(`marketing.referrals.faq.${k}.a`)}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <div className="card-surface mt-12 flex flex-wrap items-center justify-between gap-4 p-6">
        <div>
          <p className="font-display text-lg font-bold">{t("marketing.referrals.get_link_title")}</p>
          <p className="text-sm text-muted-foreground">
            {t("marketing.referrals.get_link_body")}
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="hot">
            <Link to="/app/referrals">
              <Flame className="size-4" /> {t("marketing.referrals.cta_open")}
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/how-it-works">
              {t("marketing.referrals.cta_how")} <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
